import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';
import {
    Typography, Button, List, ListItem, ListItemText,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, CircularProgress, Divider, Card,
    Tabs, Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';

const emptyForm = () => ({
    IncidentType: '',
    startDate: moment().format('YYYY-MM-DD'),
    startTime: moment().format('HH:mm'),
    endDate: moment().format('YYYY-MM-DD'),
    endTime: moment().add(1, 'minute').format('HH:mm'),
    noteText: '',
    artifactFile: null,
    artifactName: '',
    artifactPreview: null,
});

function DateTimeRow({ label, dateValue, timeValue, onDateChange, onTimeChange }) {
    return (
        <div style={{ marginTop: 12 }}>
            <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>{label}</Typography>
            <div style={{ display: 'flex', gap: 10 }}>
                <TextField type="date" size="small" variant="outlined" value={dateValue}
                    onChange={e => onDateChange(e.target.value)} InputLabelProps={{ shrink: true }} style={{ flex: 1 }} />
                <TextField type="time" size="small" variant="outlined" value={timeValue}
                    onChange={e => onTimeChange(e.target.value)} InputLabelProps={{ shrink: true }} style={{ flex: 1 }} />
            </div>
        </div>
    );
}

function VoiceRecorder({ onRecordingComplete }) {
    const [recording, setRecording] = useState(false);
    const [recorded, setRecorded] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const audioBlobRef = useRef(null);
    const audioUrlRef = useRef(null);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            chunksRef.current = [];
            const mr = new MediaRecorder(stream);
            mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            mr.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                audioBlobRef.current = blob;
                audioUrlRef.current = URL.createObjectURL(blob);
                onRecordingComplete(blob);
                setRecorded(true);
                stream.getTracks().forEach(t => t.stop());
            };
            mr.start();
            mediaRecorderRef.current = mr;
            setRecording(true);
            setElapsed(0);
            timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
        } catch (err) {
            alert('Microphone access denied.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && recording) {
            mediaRecorderRef.current.stop();
            clearInterval(timerRef.current);
            setRecording(false);
        }
    };

    const play = () => {
        if (audioUrlRef.current) {
            const audio = new Audio(audioUrlRef.current);
            audio.play();
        }
    };

    const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

    return (
        <div style={{ border: '1px solid #ddd', borderRadius: 4, padding: 12, marginTop: 10 }}>
            <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8 }}>Voice Note</Typography>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <IconButton color={recording ? 'error' : 'default'} onClick={recording ? stopRecording : startRecording} size="small">
                    {recording ? <StopIcon /> : <MicIcon />}
                </IconButton>
                <Typography variant="body2" color={recording ? 'error' : 'textSecondary'} style={{ minWidth: 40 }}>
                    {fmt(elapsed)}
                </Typography>
                {recorded && !recording && (
                    <IconButton onClick={play} size="small" title="Play recording">
                        <PlayArrowIcon />
                    </IconButton>
                )}
                <Typography variant="caption" color="textSecondary">
                    {recording ? 'Recording...' : recorded ? 'Recording ready' : 'Tap mic to record'}
                </Typography>
            </div>
        </div>
    );
}

function IncidentDetailModal({ incident, open, onClose }) {
    const [audioUrl, setAudioUrl] = useState(null);

    useEffect(() => {
        if (!open || !incident?.audio_note) { setAudioUrl(null); return; }
        const bufferData = incident.audio_note?.data ?? incident.audio_note;
        const blob = new Blob([new Uint8Array(bufferData)], { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [open, incident]);

    if (!incident) return null;

    const hasAttachments = incident.comment_text || incident.audio_note || incident.artifact_url;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{incident.IncidentType || '(no type)'}</DialogTitle>
            <DialogContent>
                {incident.IncidentStartTime && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>Start Time:</span> {moment(incident.IncidentStartTime).format('MM/DD/YYYY h:mm A')}
                    </Typography>
                )}
                {incident.IncidentEndTime && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>End Time:</span> {moment(incident.IncidentEndTime).format('MM/DD/YYYY h:mm A')}
                    </Typography>
                )}
                {incident.addedOn && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>Recorded On:</span> {moment(incident.addedOn).format('MM/DD/YYYY')}
                    </Typography>
                )}

                {hasAttachments && <Divider style={{ margin: '12px 0' }} />}

                {incident.comment_text && (
                    <div style={{ marginBottom: 12 }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 4 }}>Note</Typography>
                        <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', background: '#f5f5f5', borderRadius: 4, padding: '8px 12px' }}>
                            {incident.comment_text}
                        </Typography>
                    </div>
                )}

                {audioUrl && (
                    <div style={{ marginBottom: 12 }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 4 }}>Voice Note</Typography>
                        <audio controls src={audioUrl} style={{ width: '100%' }} />
                    </div>
                )}

                {incident.artifact_url && (
                    <div style={{ marginBottom: 4 }}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 4 }}>Attachment</Typography>
                        <img
                            src={incident.artifact_url}
                            alt={incident.artifact_file_name || 'attachment'}
                            style={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 4, border: '1px solid #eee' }}
                        />
                        {incident.artifact_file_name && (
                            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
                                {incident.artifact_file_name}
                            </Typography>
                        )}
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function IncidentSection({ incidents, customer_id, addedBy, onAdd, onDelete }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailIncident, setDetailIncident] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const audioBlobRef = useRef(null);

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const handleOpen = () => {
        setForm(emptyForm());
        setActiveTab(0);
        audioBlobRef.current = null;
        setDialogOpen(true);
    };

    const handleArtifactFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => set('artifactPreview', ev.target.result);
        reader.readAsDataURL(file);
        set('artifactFile', file);
        set('artifactName', file.name);
    };

    const handleSave = async () => {
        if (!form.IncidentType.trim()) {
            alert('Unusual Incident Type is required.');
            setActiveTab(0);
            return;
        }
        setSaving(true);
        try {
            const startDateTime = form.startDate && form.startTime
                ? moment(`${form.startDate} ${form.startTime}`, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DDTHH:mm:ss')
                : null;
            const endDateTime = form.endDate && form.endTime
                ? moment(`${form.endDate} ${form.endTime}`, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DDTHH:mm:ss')
                : null;

            // 1. Create incident
            const incRes = await axios.post(APIURL + `/customer_incident`, {
                customer_id, addedBy,
                IncidentType: form.IncidentType,
                IncidentStartTime: startDateTime,
                IncidentEndTime: endDateTime,
            });
            const incidentId = incRes.data?.id;
            const idUpdates = {};

            // 2. Save note text
            if (form.noteText.trim() && incidentId) {
                try {
                    const noteRes = await axios.post(APIURL + `/comments`, {
                        cus_id: customer_id,
                        emp_id: addedBy,
                        comment_text: form.noteText,
                        note_title: `Incident #${incidentId} Note`,
                    });
                    const commentId = noteRes.data?.comment_data?.comment_id;
                    if (commentId) idUpdates.IncidentCommentID = commentId;
                } catch (err) {
                    console.error('Failed to save note:', err);
                }
            }

            // 3. Save voice note
            if (audioBlobRef.current && incidentId) {
                try {
                    const audioForm = new FormData();
                    audioForm.append('audio_note', audioBlobRef.current, 'voice_note.webm');
                    audioForm.append('emp_id', addedBy);
                    audioForm.append('customer_id', customer_id);
                    audioForm.append('note_title', `Incident #${incidentId} Voice Note`);
                    const audioRes = await axios.post(APIURL + `/audio_note`, audioForm);
                    const audioId = audioRes.data?.data?.[0]?.[''];
                    if (audioId) idUpdates.IncidentAudioNoteID = audioId;
                } catch (err) {
                    console.error('Failed to save voice note:', err);
                }
            }

            // 4. Save artifact
            if (form.artifactFile && incidentId) {
                try {
                    const artForm = new FormData();
                    artForm.append('snapshot', form.artifactFile, form.artifactName);
                    artForm.append('file_name', form.artifactName);
                    artForm.append('customer_id', customer_id);
                    const artRes = await axios.post(APIURL + `/artifacts`, artForm);
                    const artId = artRes.data?.id;
                    if (artId) idUpdates.IncidentArtifactID = artId;
                } catch (err) {
                    console.error('Failed to save artifact:', err);
                }
            }

            // 5. Link IDs back to incident
            if (incidentId && Object.keys(idUpdates).length > 0) {
                await axios.put(APIURL + `/customer_incident/${incidentId}`, idUpdates);
            }

            onAdd({
                customer_incident_id: incidentId || Date.now(),
                customer_id,
                IncidentType: form.IncidentType,
                IncidentStartTime: startDateTime,
                IncidentEndTime: endDateTime,
                IncidentCommentID: idUpdates.IncidentCommentID || null,
                IncidentAudioNoteID: idUpdates.IncidentAudioNoteID || null,
                IncidentArtifactID: idUpdates.IncidentArtifactID || null,
                addedOn: new Date().toISOString(),
            });
            setDialogOpen(false);
        } catch (err) {
            console.error(err);
        }
        setSaving(false);
    };

    const handleDelete = async (id) => {
        setDeleting(id);
        try {
            await axios.delete(APIURL + `/customer_incident/${id}`);
            onDelete(id);
        } catch (err) {
            console.error(err);
        }
        setDeleting(null);
    };

    return (
        <Card elevation={3} style={{ marginBottom: 5, padding: 12, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Typography variant="subtitle1" fontWeight="bold">INCIDENTS</Typography>
                <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={handleOpen}>Add</Button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
                {incidents.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">No incidents recorded.</Typography>
                ) : (
                    <List dense disablePadding style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {incidents.map((inc, i) => (
                            <ListItem
                                key={inc.customer_incident_id || i}
                                disablePadding
                                onClick={() => setDetailIncident(inc)}
                                sx={{
                                    px: 1,
                                    borderRadius: 1,
                                    border: '1px solid #e8e8e8',
                                    cursor: 'pointer',
                                    bgcolor: '#fafafa',
                                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                                    '&:hover': { bgcolor: '#EBF5FB', borderColor: '#90CAF9' },
                                }}
                            >
                                <ListItemText
                                    primary={inc.IncidentType || '(no type)'}
                                    secondary={inc.addedOn ? moment(inc.addedOn).format('MM/DD/YYYY') : ''}
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                />
                                <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled', mr: 0.5 }} />
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(inc.customer_incident_id); }} disabled={deleting === inc.customer_incident_id}>
                                    {deleting === inc.customer_incident_id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" color='error' />}
                                </IconButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </div>
            <IncidentDetailModal incident={detailIncident} open={!!detailIncident} onClose={() => setDetailIncident(null)} />

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Incident</DialogTitle>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} style={{ borderBottom: '1px solid #e0e0e0', paddingLeft: 16 }}>
                    <Tab label="Incident" value={0} />
                    <Tab label="Notes" value={1} />
                    <Tab label="Artifacts" value={2} />
                </Tabs>
                <DialogContent>
                    {/* Tab 0: Incident */}
                    <div style={{ display: activeTab === 0 ? 'block' : 'none' }}>
                        <TextField
                            label="Unusual Incident Type"
                            fullWidth size="small" variant="outlined"
                            value={form.IncidentType}
                            onChange={e => set('IncidentType', e.target.value)}
                            required style={{ marginTop: 10 }}
                        />
                        <DateTimeRow
                            label="Unusual Incident Start Time"
                            dateValue={form.startDate} timeValue={form.startTime}
                            onDateChange={v => set('startDate', v)} onTimeChange={v => set('startTime', v)}
                        />
                        <DateTimeRow
                            label="Unusual Incident End Time"
                            dateValue={form.endDate} timeValue={form.endTime}
                            onDateChange={v => set('endDate', v)} onTimeChange={v => set('endTime', v)}
                        />
                    </div>

                    {/* Tab 1: Notes + Voice — always mounted to preserve recording state */}
                    <div style={{ display: activeTab === 1 ? 'block' : 'none' }}>
                        <TextField
                            label="Notes"
                            fullWidth size="small" variant="outlined" multiline minRows={4}
                            value={form.noteText}
                            onChange={e => set('noteText', e.target.value)}
                            style={{ marginTop: 10 }}
                        />
                        <VoiceRecorder onRecordingComplete={(blob) => { audioBlobRef.current = blob; }} />
                    </div>

                    {/* Tab 2: Artifacts */}
                    <div style={{ display: activeTab === 2 ? 'block' : 'none', marginTop: 10 }}>
                        {!form.artifactFile ? (
                            <Button variant="outlined" component="label" startIcon={<AttachFileIcon />} style={{ marginTop: 10 }}>
                                Upload Picture
                                <input type="file" accept="image/*" hidden onChange={handleArtifactFile} />
                            </Button>
                        ) : (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                    <TextField
                                        label="File Name"
                                        size="small" variant="outlined"
                                        value={form.artifactName}
                                        onChange={e => set('artifactName', e.target.value)}
                                        style={{ flex: 1 }}
                                    />
                                    <IconButton size="small" onClick={() => { set('artifactFile', null); set('artifactPreview', null); set('artifactName', ''); }}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </div>
                                {form.artifactPreview && (
                                    <img
                                        src={form.artifactPreview}
                                        alt="preview"
                                        style={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 4, border: '1px solid #eee' }}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    {activeTab < 2
                        ? <Button variant="outlined" onClick={() => setActiveTab(t => t + 1)}>Next</Button>
                        : <Button onClick={handleSave} variant="contained" disabled={saving}>
                            {saving ? <CircularProgress size={20} /> : 'Add Incident'}
                        </Button>
                    }
                </DialogActions>
            </Dialog>
        </Card>
    );
}

export default IncidentSection;
